/**
 * Dokumente → Verträge (TQ #337, Auftrag 2026-09-25).
 *
 * GET /api/org/:slug/documents/contracts → { documents: ContractDocument[] }.
 * Feldnamen gegen den echten Go-Handler geprüft (it-sce-platform, Branch
 * feat/337-cockpit-dokumente-tab, services/portal-api/internal/handlers/
 * org_erp_contracts.go, Stand 30.09.2026):
 *
 *   kind: 'contract' | 'subscription'
 *   name, title, status: string
 *   start_date: string; end_date?: string (omitempty)
 *   pdf_available: boolean (nur kind='contract' + signiert)
 *   kuendigungsfrist: string (serverseitig IMMER "nicht erfasst" — kein
 *     ERPNext-Feld dafür bestätigt, siehe Handler-Kommentar)
 *
 * TQ #960 (AVV-Download "tut nichts", Patric 25.10.): der fruehere Weg baute einen
 * <a download> auf eine Blob-URL, klickte ihn und widerrief die URL sofort im selben
 * Tick (URL.revokeObjectURL direkt nach a.click()). Je nach Browser (v. a. Safari/
 * Firefox) bricht der Download dann still ab. Fehler lasen zudem data.error auf dem
 * Blob-Body (immer "Unbekannt"). Jetzt: "Anzeigen" oeffnet das PdfViewerModal; die
 * Blob-URL lebt bis zum Schliessen, "PDF speichern" ist ein Anker auf diese URL, Fehler
 * (inkl. 403 mit forbiddenText) stehen im Modal.
 */
import { useEffect, useState } from 'react'
import { useAuth } from '@it-sce/ui/contexts/KeycloakAuthContext'
import { useToast } from '../../components/ui'
import PdfViewerModal from '../../components/PdfViewerModal'
import { forbiddenText } from '../../lib/forbiddenText'

interface Organization {
  id: number
  name: string
  slug: string
  public_token?: string
  member_role: string
}

interface ContractDocument {
  kind: 'contract' | 'subscription'
  name: string
  title: string
  status: string
  start_date: string
  end_date?: string
  pdf_available: boolean
  kuendigungsfrist: string
}

interface Props {
  organization: Organization
}

const fmtDate = (s?: string) =>
  s ? new Date(s).toLocaleDateString('de-DE', { day: '2-digit', month: '2-digit', year: 'numeric' }) : '–'

const kindLabel = (kind: string) => (kind === 'contract' ? 'Vertrag' : kind === 'subscription' ? 'Abo' : kind)

export default function Contracts({ organization }: Props) {
  const { api: keycloakApi } = useAuth()
  const { showToast } = useToast()
  const slugOrToken = organization.public_token || organization.slug

  const [documents, setDocuments] = useState<ContractDocument[]>([])
  const [loading, setLoading] = useState(true)
  // Backend meldet unavailable=no_access, wenn ERPNext dem Read-Bot das Lesen von Vertraegen verweigert.
  const [unavailable, setUnavailable] = useState(false)
  const [viewerName, setViewerName] = useState<string | null>(null)

  useEffect(() => {
    let cancelled = false
    async function load() {
      setLoading(true)
      setUnavailable(false)
      try {
        const r = await keycloakApi!.get(`/api/org/${slugOrToken}/documents/contracts`)
        if (cancelled) return
        const list = r.data?.documents ?? []
        setDocuments(Array.isArray(list) ? list : [])
        setUnavailable(r.data?.unavailable === 'no_access')
      } catch (e) {
        if (cancelled) return
        const err = e as { response?: { status?: number; data?: { error?: string } } }
        if (err.response?.status !== 404) {
          showToast('error', forbiddenText(e) ?? `Verträge konnten nicht geladen werden: ${err.response?.data?.error ?? 'Unbekannt'}`)
        }
        setDocuments([])
      } finally {
        if (!cancelled) setLoading(false)
      }
    }
    void load()
    return () => { cancelled = true }
  }, [slugOrToken, keycloakApi, showToast])

  async function loadPdf(name: string): Promise<Blob> {
    const res = await keycloakApi!.get(`/api/org/${slugOrToken}/documents/contracts/pdf`, {
      responseType: 'blob', params: { name },
    })
    return res.data as Blob
  }

  return (
    <div>
      {/* Kein Herkunftshinweis ("soweit in ERPNext ... hinterlegt"): interne Info, nicht fuer den Kunden (Patric 26.10., TQ #960). */}
      {loading ? (
        <p style={{ color: '#6b7280' }}>Lade …</p>
      ) : unavailable ? (
        <div role="status" style={{ padding: '1.25rem 1rem', background: '#f9fafb', borderRadius: 6, color: '#6b7280', textAlign: 'center' }}>
          Vertragsdaten sind für {organization.name} derzeit nicht abrufbar.
        </div>
      ) : documents.length === 0 ? (
        <div style={{ padding: '1.25rem 1rem', background: '#f9fafb', borderRadius: 6, color: '#6b7280', textAlign: 'center' }}>
          Keine Verträge für {organization.name} hinterlegt.
        </div>
      ) : (
        <div style={{ background: '#fff', border: '1px solid #e5e7eb', borderRadius: 8, overflow: 'hidden' }}>
          <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: '0.875rem' }}>
            <thead>
              <tr style={{ background: '#f9fafb', borderBottom: '1px solid #e5e7eb', textAlign: 'left' }}>
                <th style={{ padding: '0.75rem 1rem' }}>Vertrag</th>
                <th style={{ padding: '0.75rem 0.5rem' }}>Art</th>
                <th style={{ padding: '0.75rem 0.5rem' }}>Status</th>
                <th style={{ padding: '0.75rem 0.5rem' }}>Laufzeit</th>
                <th style={{ padding: '0.75rem 0.5rem' }}>Kündigungsfrist</th>
                <th style={{ padding: '0.75rem 1rem', textAlign: 'right' }}>PDF</th>
              </tr>
            </thead>
            <tbody>
              {documents.map((d) => (
                <tr key={d.name} style={{ borderBottom: '1px solid #f3f4f6' }}>
                  <td style={{ padding: '0.75rem 1rem' }}>
                    <div>{d.title || d.name}</div>
                    <code style={{ fontSize: '0.75rem', color: '#9ca3af' }}>{d.name}</code>
                  </td>
                  <td style={{ padding: '0.75rem 0.5rem', color: '#6b7280' }}>{kindLabel(d.kind)}</td>
                  <td style={{ padding: '0.75rem 0.5rem', color: '#6b7280' }}>{d.status || '–'}</td>
                  <td style={{ padding: '0.75rem 0.5rem', color: '#6b7280' }}>
                    {fmtDate(d.start_date)} – {d.end_date ? fmtDate(d.end_date) : 'laufend'}
                  </td>
                  <td style={{ padding: '0.75rem 0.5rem', color: '#9ca3af' }}>{d.kuendigungsfrist}</td>
                  <td style={{ padding: '0.75rem 1rem', textAlign: 'right' }}>
                    {d.kind === 'contract' && d.pdf_available ? (
                      <button
                        type="button"
                        onClick={() => setViewerName(d.name)}
                        title="PDF anzeigen"
                        style={{
                          padding: '4px 10px', border: '1px solid #d1d5db',
                          borderRadius: 4, background: '#fff', color: '#1f2937',
                          cursor: 'pointer', fontSize: '0.8125rem',
                        }}>
                        📄 Anzeigen
                      </button>
                    ) : (
                      <span style={{ fontSize: '0.75rem', color: '#9ca3af' }}>kein PDF</span>
                    )}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}

      {viewerName && (
        <PdfViewerModal
          key={viewerName}
          title={`Vertrag ${viewerName}`}
          filename={viewerName}
          load={() => loadPdf(viewerName)}
          onClose={() => setViewerName(null)}
        />
      )}
    </div>
  )
}
